import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import * as SBService from "../../../helpers/sportbookService";
import { userState } from "../../../states/common";
import FeePaymentModal from "../../../components/league/FeePaymentModal";
import { useMyLeagueStatus } from "../../../hooks/league";
import "../shared/league.css";
import { trangThaiDangKy } from "./trangThaiDangKy";

/**
 * Layout chi tiết giải CHUNG (mọi loại giải, chưa đăng ký) — cùng khung với Swiss:
 * cover + tên + thông tin ngắn + chọn địa điểm + card đăng ký (dock), và 2 tab
 * Thông tin chung / Đã đăng ký. Thể lệ & giải thưởng lấy từ admin.
 * Cơ chế phí (isFeePaid) dùng chung cho mọi loại giải.
 */

const TYPE_LABEL: Record<string, string> = {
  "ROUND-ROBIN": "Vòng tròn",
  ELIMINATION: "Loại trực tiếp",
  TEAM: "Đồng đội",
  MASTER: "Master",
  SWISS: "Swiss",
};

const money = (v: any) => {
  const n = Number(String(v).replace(/\D/g, ""));
  return isNaN(n) ? v : n.toLocaleString("vi-VN");
};
const range = (a: any, b: any) =>
  a && b ? `${moment(a).format("DD/MM")} – ${moment(b).format("DD/MM/YYYY")}` : "Chưa công bố";

export default function LeaguePreJoin({
  league,
  banner,
  notAbleToJoin,
}: {
  league: any;
  banner?: string;
  notAbleToJoin: boolean;
}) {
  const navigate = useNavigate();
  const [user] = useRecoilState(userState);
  const [tab, setTab] = useState<"info" | "reg">("info");
  const [venuesSheetOpen, setVenuesSheetOpen] = useState(false);
  const [players, setPlayers] = useState<any[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [feeModalOpen, setFeeModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const venues: any[] = league.venues || [];

  useEffect(() => {
    if (!league.id) return;
    SBService.getLeaguePlayersById(league.id, 50, 1)
      .then((rsp: any) => setPlayers(rsp?.data || []))
      .catch(() => setPlayers([]));
  }, [league.id]);

  const registered = !!user?.leagues?.find((l: any) => l.id === league.id);

  /* Đã đăng ký một giải CÓ thu phí mà BTC chưa xác nhận đã nhận: người chơi cần
     mã QR và nội dung chuyển khoản. Hook trả null khi chưa đăng nhập hoặc lỗi
     mạng, nên mặc định là không hiện gì - y như giải không thu phí. */
  const myStatus = useMyLeagueStatus(league.id, !!user.id);
  const needsFee = !!myStatus?.registered && !!myStatus?.requiresFee && !myStatus?.feePaid;

  /* Luật nút đăng ký nằm ở trangThaiDangKy (có test): chỉ khoá theo khung đăng ký,
     không theo ngày bắt đầu giải - giải một ngày phải đăng ký được trong ngày. */
  const phase = useMemo(
    () => trangThaiDangKy(league, { daThamGia: registered, khongDuDieuKien: notAbleToJoin }),
    [league, registered, notAbleToJoin],
  );

  const handleJoin = async () => {
    try {
      setBusy(true);
      const rsp: any = await SBService.joinLeagueById(league.id);
      const joined = rsp?.data ?? rsp;
      // Giải thu phí: đăng ký xong vẫn phải chờ BTC xác nhận đóng phí mới chính thức.
      if (joined?.requiresFee && !joined?.feePaid) {
        toast.success("Đăng ký thành công. Vui lòng đóng phí và chờ ban tổ chức xác nhận.");
        setTimeout(() => window.location.reload(), 1500);
        return;
      }
      /* Đăng ký xong thì đưa thẳng sang màn đã đăng ký (mặc định tab Thi đấu).
         Reload tại chỗ sẽ dựng lại đúng màn CHƯA đăng ký, vì /league/:id luôn
         render LeaguePreJoin — người dùng vừa đăng ký xong lại nhìn thấy nút
         đăng ký. */
      navigate(`/league/${league.id}/detail`);
    } catch (error: any) {
      const err = error?.response?.data?.error;
      const msg: Record<string, string> = {
        playerNotCalibrated: "Tài khoản chưa hoàn tất khảo hạch không thể tham gia giải đấu.",
        playerRankIsNotAllowed: "Không thể tham gia giải đấu do xếp hạng không phù hợp.",
      };
      toast.error(msg[err] || "Không thể tham gia giải đấu");
    } finally {
      setModalOpen(false);
      setBusy(false);
    }
  };


  const Info = () => (
    <>
      <div className="sec">
        <div className="infoline"><div className="ic">📅</div><div><div className="k">Đăng ký</div><div className="v acc">{range(league.startRegistrationDate, league.endRegistrationDate)}</div></div></div>
        <div className="divider" />
        <div className="infoline"><div className="ic">🎱</div><div><div className="k">Thi đấu</div><div className="v">{range(league.startDate, league.endDate)}</div></div></div>
      </div>
      <div className="jhead">Giải thưởng</div>
      <div className="sec">
        {league.leaguePrizes && league.leaguePrizes.length > 0 ? (
          <>
            <div className="prizehead"><span>Thứ hạng</span><span style={{ textAlign: "right" }}>Tiền mặt</span><span>Hiện vật</span></div>
            {league.leaguePrizes.map((p: any, i: number) => (
              <div key={i} className="prizerow"><span className="p1">{p.label}</span><span className="p2">{p.prizeInCash ? `${money(p.prizeInCash)} đ` : "—"}</span><span className="small">{p.prizeInKind || "—"}</span></div>
            ))}
          </>
        ) : (
          <div className="tiny">Ban tổ chức chưa công bố cơ cấu giải thưởng.</div>
        )}
      </div>
      <div className="jhead">Thể lệ thi đấu</div>
      <div className="sec rulebody">
        <div className="metarow" style={{ marginBottom: 10 }}>
          <span className="chip solid">{TYPE_LABEL[league.type] || league.type}</span>
          <span className="dot" /><span>{venues.length} địa điểm</span>
          {league.fee ? <><span className="dot" /><span>Lệ phí {money(league.fee)} đ</span></> : null}
        </div>
        {league.rule ? (
          <div className="raw-css" dangerouslySetInnerHTML={{ __html: league.rule }} />
        ) : (
          <div className="tiny">Ban tổ chức chưa công bố thể lệ chi tiết.</div>
        )}
      </div>
    </>
  );

  const Registered = () => {
    const total = league.numberParticipants || players.length;
    return (
      <div className="sec" style={{ padding: "18px 12px", textAlign: "center" }}>
        <div className="tiny">
          {players.length > 0
            ? `${players.length}/${total} cơ thủ đã đăng ký`
            : "Chưa có cơ thủ nào đăng ký"}
        </div>
      </div>
    );
  };

  const VenueRow = ({ v }: { v: any }) => (
    <div className="venue" style={{ cursor: "default" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="vname">{v.name}</div>
        {v.address ? <div className="vaddr">{v.address}</div> : null}
      </div>
    </div>
  );

  return (
    <div className="biadi">
      <div className="navbar">
        <button className="back" onClick={() => navigate(-1)}>‹</button>
        <span className="ttl">Thông tin giải đấu</span>
      </div>
      <div className="pad">
        {/* Giải không có banner thì bỏ hẳn khối này: một khung gradient rỗng đắp
            tên giải lên chỉ đẩy nội dung thật xuống dưới màn hình. */}
        {banner ? (
          <div className="cover">
            <img alt={league.name} src={banner} />
            <span className="fmt chip solid">{TYPE_LABEL[league.type] || league.type}</span>
          </div>
        ) : null}
        <h1 className="jname">{league.name}</h1>
        <div className="metarow" style={{ marginTop: 8 }}>
          <span>{TYPE_LABEL[league.type] || league.type}</span>
          <span className="dot" /><span>{venues.length} địa điểm</span>
          {league.numberParticipants ? <><span className="dot" /><span>{players.length}/{league.numberParticipants} cơ thủ</span></> : null}
          {league.fee ? <><span className="dot" /><span>Lệ phí {money(league.fee)} đ</span></> : null}
        </div>

        {venues.length > 0 && (
          <>
            <div className="sec-h"><span className="label">Địa điểm thi đấu</span><span className="tiny">{venues.length} địa điểm</span></div>
            <div>
              {venues.slice(0, 3).map((v) => (
                <VenueRow key={v.id} v={v} />
              ))}
            </div>
            {venues.length > 3 && (
              <button className="btn btn-ghost btn-block" style={{ marginTop: 8 }} onClick={() => setVenuesSheetOpen(true)}>
                Xem thêm {venues.length - 3} địa điểm
              </button>
            )}
          </>
        )}

        <div className="tabs">
          <button className={tab === "reg" ? "" : "on"} onClick={() => setTab("info")}>Thông tin chung</button>
          <button className={tab === "reg" ? "on" : ""} onClick={() => setTab("reg")}>Đã đăng ký</button>
        </div>
        <div className="tabpane">{tab === "reg" ? <Registered /> : <Info />}</div>
        <div className="actionbar-spacer" />
      </div>

      <div className="league-dock">
        {league.fee ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <span className="tiny" style={{ marginLeft: "auto" }}>Lệ phí {money(league.fee)} đ</span>
          </div>
        ) : null}
        {/* Xem giải không cần đăng nhập, nhưng đăng ký thì có: đưa khách sang
            màn đăng nhập thay vì mở hộp xác nhận rồi mới báo lỗi. */}
        {needsFee ? (
          /* Thay chỗ nút "Đã tham gia" (đang disabled, không làm được gì) bằng
             việc cần làm thật: đóng phí. Trạng thái chờ nói bằng một dòng chữ. */
          <>
            <div className="tiny" style={{ marginBottom: 8 }}>Đã đăng ký · chờ ban tổ chức xác nhận đóng phí</div>
            <button className="btn btn-primary btn-block" onClick={() => setFeeModalOpen(true)}>
              Đóng phí
            </button>
          </>
        ) : user.id ? (
          <button className="btn btn-primary btn-block" disabled={!phase.canRegister || busy} onClick={() => setModalOpen(true)}>
            {phase.cta}
          </button>
        ) : (
          <button className="btn btn-primary btn-block" onClick={() => navigate("/auth/login")}>
            Đăng nhập để tham gia
          </button>
        )}
      </div>

      {modalOpen && (
        <div className="modalwrap">
          <div className="modalbd" onClick={() => setModalOpen(false)} />
          <div className="modal">
            <div className="label" style={{ marginBottom: 10 }}>Xác nhận tham gia</div>
            <div className="callout">
              <div style={{ fontWeight: 600, marginBottom: 6 }}>{league.name}</div>
              {league.fee ? (
                <div className="small" style={{ marginTop: 8, color: "#E9CFC9" }}>
                  Lệ phí {money(league.fee)} đ{" "}
                  {league.feeQrImage || league.feeTransferNote
                    ? "— đăng ký xong bấm Đóng phí để lấy mã QR và nội dung chuyển khoản."
                    : "đóng tại địa điểm khi check-in."}{" "}
                  Đăng ký chỉ chính thức sau khi BTC xác nhận đóng phí.
                </div>
              ) : null}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} disabled={busy} onClick={handleJoin}>Xác nhận</button>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setModalOpen(false)}>Để sau</button>
            </div>
          </div>
        </div>
      )}

      {feeModalOpen && (
        <FeePaymentModal
          league={league}
          feeDeadline={myStatus?.feeDeadline}
          phone={user?.phone}
          billUploaded={myStatus?.billUploaded}
          onClose={() => setFeeModalOpen(false)}
        />
      )}

      {venuesSheetOpen && (
        <div className="sheetwrap">
          <div className="sheetbd" onClick={() => setVenuesSheetOpen(false)} />
          <div className="sheet">
            <div className="grab" />
            <div className="sc">
              <div className="sttl">Địa điểm thi đấu</div>
              <div className="small">{venues.length} địa điểm</div>
              <div style={{ marginTop: 12, maxHeight: "56vh", overflow: "auto" }}>
                {venues.map((v) => (
                  <VenueRow key={v.id} v={v} />
                ))}
              </div>
              <button className="btn btn-ghost btn-block" style={{ marginTop: 12 }} onClick={() => setVenuesSheetOpen(false)}>
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
